/**
 * Publish Preview artifacts service.
 *
 * Reads the Phase 3 backend artifacts:
 *   GET /publish-preview/{simulationId}/manifest
 *   GET /publish-preview/{simulationId}/summary
 *   GET /publish-preview/{simulationId}/decisions/{stage}?pageSize=&cursor=
 *   GET /publish-preview/{simulationId}/vehicle-data?bucket=&pageSize=&cursor=&search=
 *   GET /publish-preview/{simulationId}/vehicle-data/vin/{vin}
 *   GET /publish-preview/{simulationId}/vehicle-groups?bucket=&pageSize=&cursor=&search=
 *   GET /publish-preview/{simulationId}/vehicle-groups/vin/{vin}
 *   GET /publish-preview/{simulationId}/listing-visibility?bucket=&pageSize=&cursor=&search=
 *   GET /publish-preview/{simulationId}/listing-visibility/vin/{vin}
 *   GET /publish-preview/{simulationId}/vins/{vin}
 *
 *   - Add typed clients only. Do NOT modify the legacy result blob path
 *     (`publishPreviewService.ts`), the Pinia store, the views, or the
 *     router. The existing UI must keep working unchanged.
 *   - Two API surfaces per endpoint:
 *       1. Strict methods (`getPublishPreview*`) that throw on any non-200.
 *          Use these when the caller wants the canonical error path.
 *       2. Fallback methods (`tryGetPublishPreview*`) that return a
 *          discriminated union. Use these when the caller wants to
 *          gracefully fall back to the legacy result blob if v2 is
 *          missing/unavailable for an expected reason.
 *
 * Fallback semantics (`try*` only):
 *   - `notFound`     — backend returned 404 with a recognized "expected
 *                       absent" code (manifest/summary/stage artifact/sim
 *                       not found). The caller should keep using the
 *                       legacy preview result.
 *   - `notAvailable` — stage decisions or vehicle-data buckets. Backend returned 400 with
 *                       `STAGE_NOT_AVAILABLE`, meaning the requested data is intentionally
 *                       unavailable for this simulation.
 *   - `unavailable`  — v2 endpoint itself isn't usable right now (network
 *                       error, 5xx, or a 404 that doesn't carry a known
 *                       code — e.g. the route is genuinely not deployed).
 *                       Caller should fall back to legacy.
 *
 * Errors that always THROW (from both strict and `try*`):
 *   - 401 / 403            — auth/authz failures, surface to user
 *   - 400 INVALID_STAGE    — programmer error (illegal stage literal)
 *   - 400 INVALID_VIN      — programmer error (empty/invalid VIN)
 *   - 400 INVALID_VIN_LENGTH
 *   - 400 INVALID_CURSOR / CURSOR_VERSION / CURSOR_MISMATCH /
 *         CURSOR_OUT_OF_RANGE
 *   - 400 MISSING_PATH_PARAM
 *
 * The cursor token is OPAQUE on the client. We never decode or inspect
 * it; we only round-trip it as a string in the `cursor` query parameter.
 */

// ---------------------------------------------------------------------------
// Local mirrors of the backend on-disk shapes.
//
// We mirror these types instead of importing from `@jazel-auto/ise-contracts`
// because the published package does not yet expose the v2 observability
// surface (the package is intentionally not a dependency of this app). 
// This mirror MUST stay in sync with:
//   - ise/ise-srp-back/src/publish_preview/observabilityArtifacts.ts
//   - ise/ise-srp-back/src/publish_preview/decisionArtifacts.ts
//   - ise/ise-srp-back/src/publish_preview/decisionArtifactReader.ts
// When `@jazel-auto/ise-contracts` ships v0.2.0 these types should be
// deleted and re-imported from there in a separate cross-repo PR.
// ---------------------------------------------------------------------------

export type PublishObservedStage =
  | 'preProcessor'
  | 'inventorySync'
  | 'fieldMapping'
  | 'feedFieldRules'
  | 'listingRules'
  | 'groups'

/** Stages currently supported by the `/decisions/{stage}` endpoint. */
export type PublishSupportedDecisionStage =
  | 'fieldMapping'
  | 'listingRules'
  | 'preProcessor'
  | 'feedFieldRules'
  | 'groups'
  | 'inventorySync'

export type PublishVehicleDataBucket = 'changed' | 'unchanged' | 'emptying'

export type PublishVehicleGroupBucket = 'added' | 'removed' | 'changed' | 'unchanged'

export type PublishVehicleGroupBucketVinRow = {
  vin: string
  addedGroupCount?: number
  removedGroupCount?: number
  groupNames?: string[]
}

export type PublishVehicleGroupCriterionDetail = {
  field: string
  operator?: string
  expectedValue?: unknown
  currentValue?: unknown
  afterPublishValue?: unknown
  currentSource?: string
  afterPublishSource?: string
  currentMatches: boolean
  afterPublishMatches: boolean
}

export type PublishVehicleGroupMemberDetail = {
  groupId: string
  groupName: string
  criteria?: PublishVehicleGroupCriterionDetail[]
}

export type PublishVehicleGroupVinDetail = {
  vin: string
  publishedGroups: PublishVehicleGroupMemberDetail[]
  afterPublishGroups: PublishVehicleGroupMemberDetail[]
  added: PublishVehicleGroupMemberDetail[]
  removed: PublishVehicleGroupMemberDetail[]
}

export type PublishListingVisibilityBucket =
  | 'changed'
  | 'willAppear'
  | 'willHide'
  | 'reasonChanged'
  | 'unchanged'
  // Website-impact categories (Publish Flow's five Run Summary metrics) share
  // the listing-visibility drilldown read path.
  | 'addedToWebsite'
  | 'removedFromWebsite'
  | 'updatedOnWebsite'
  | 'madeVisible'
  | 'hidden'

/** The five website-impact categories mirrored from Publish Flow. */
export type PublishWebsiteImpactBucket =
  | 'addedToWebsite'
  | 'removedFromWebsite'
  | 'updatedOnWebsite'
  | 'madeVisible'
  | 'hidden'

/**
 * Unified website-impact dataset: predictive equivalents of Publish Flow's
 * five Run Summary categories. Counts here are computed from the same rows
 * that back the drilldown bucket artifacts, so Summary / Website Publish
 * Outcome / View VINs modal always agree. Categories are NOT mutually
 * exclusive; `changedLiveVinCount` is the deduped union.
 */
/**
 * Per-scope counts for the five website-impact categories. Preview-only binary
 * origin split (Feed-applied vs Manual). For every category
 * `feedApplied[c] + manual[c]` equals the matching total.
 */
export type PublishWebsiteImpactScopeCounts = {
  addedToWebsite: number
  removedFromWebsite: number
  updatedOnWebsite: number
  madeVisible: number
  hidden: number
}

export type PublishWebsiteImpactSummary = {
  addedToWebsite: number
  removedFromWebsite: number
  updatedOnWebsite: number
  madeVisible: number
  hidden: number
  changedLiveVinCount: number
  /**
   * Binary origin split of the five totals. Present on current artifacts;
   * absent on legacy artifacts that predate origin classification.
   */
  feedApplied?: PublishWebsiteImpactScopeCounts
  manual?: PublishWebsiteImpactScopeCounts
}

export type PublishListingVisibilityBucketVinRow = {
  vin: string
  beforeDecision?: 'listed' | 'delisted'
  afterDecision?: 'listed' | 'delisted'
  beforeReason?: string
  afterReason?: string
}

export type PublishListingVisibilityMatchedRuleDetail = {
  ruleId?: string
  ruleName?: string
  field?: string
  operator?: string
  expectedValue?: unknown
  actualValue?: unknown
}

export type PublishStageAvailability =
  | 'available'
  | 'unsupported'
  | 'deferred'
  | 'notConfigured'
  | 'notApplicable'

export type PublishDecisionStatus = 'changed' | 'unchanged' | 'skipped' | 'error'

export type PublishDecisionBranch = 'live' | 'afterPublish' | 'actualPublish' | 'feedIntake'

export type PublishStageCounts = {
  evaluated: number
  changed: number
  unchanged: number
  skipped: number
  error: number
}

export type PublishArtifactPointer = {
  bucket: string
  key: string
  contentType: 'application/json' | 'application/x-ndjson'
  compression?: 'gzip'
  recordCount?: number
  byteSize?: number
  status?: 'available' | 'missing' | 'partial' | 'corrupt'
  errorMessage?: string
}

export type PublishStageArtifactSummary = {
  status: 'available' | 'missing' | 'partial' | 'corrupt' | 'notRequired'
  artifactCount: number
  recordCount: number
  byteSize?: number
  pointers?: PublishArtifactPointer[]
  message?: string
}

export type PublishFeedFieldRulesDiagnostics = {
  evaluated: number
  appliedRuleCount: number
  affectedVehicles: number
  fieldsChanged: Record<string, number>
  actionCounts: Record<string, number>
}

export type PublishPreProcessorFieldValueCount = {
  value: string
  count: number
  expected?: boolean
}

export type PublishPreProcessorConfiguredFieldDiagnostics = {
  field: string
  label: string
  expectedValues: string[]
  valuesSeen: PublishPreProcessorFieldValueCount[]
  otherCount: number
  supported: boolean
}

export type PublishPreProcessorFilterDiagnostics = {
  label: string
  criteria: string[]
  matchCount: number
  rowCount?: number
  audit?: PublishPreProcessorVinAudit
}

export type PublishPreProcessorFeedFileReference = {
  bucket: string
  key: string
  fileName: string
  lastModified?: string
  byteSize?: number
  origin?: 'jads' | 'mirrorFallback' | 'sharedInventoryMirror'
}

export type PublishPreProcessorVinAudit = {
  rowCount: number
  uniqueVinCount: number
  duplicateVinRowCount: number
  invalidVinRowCount: number
}

export type PublishPreProcessorDiagnostics = {
  mode: 'groupSite' | 'sharedInventory'
  input?: {
    feed: {
      source: string
      vehicleCount: number
      file?: PublishPreProcessorFeedFileReference
      files?: PublishPreProcessorFeedFileReference[]
    }
    audit?: PublishPreProcessorVinAudit
  }
  output?: {
    feed: {
      source: string
      vehicleCount: number
      file?: PublishPreProcessorFeedFileReference
    }
    audit?: PublishPreProcessorVinAudit
    changed?: boolean
  }
  previewOutput?: {
    feed: {
      source: string
      vehicleCount: number
      file?: PublishPreProcessorFeedFileReference
    }
    audit: PublishPreProcessorVinAudit
  }
  process?: {
    matchedRowCount: number
    matchedUniqueVinCount: number
    matchedDuplicateVinRowCount: number
    matchedInvalidVinRowCount: number
  }
  feed: {
    source: string
    vehicleCount: number
    file?: PublishPreProcessorFeedFileReference
    files?: PublishPreProcessorFeedFileReference[]
  }
  configuredFilterCount: number
  totalUniqueMatches: number
  fields: PublishPreProcessorConfiguredFieldDiagnostics[]
  filters: PublishPreProcessorFilterDiagnostics[]
  explanation: string
}

export type PublishStageSummary = {
  stage: PublishObservedStage
  availability: PublishStageAvailability
  counts: PublishStageCounts
  artifacts: PublishStageArtifactSummary
  diagnostics?: PublishPreProcessorDiagnostics
  feedFieldRulesDiagnostics?: PublishFeedFieldRulesDiagnostics
  deferredReason?: string
  unsupportedReason?: string
}

export type PublishFieldMappingAuditTransitionSummary = {
  field: string
  beforeValue: string
  afterValue: string
  affectedVehicles: number
  feedsInvolved: string[]
  previousSources?: string[]
  newSources?: string[]
  sampleVins: string[]
}

export type PublishVehicleDataBucketSummary = {
  changedVehicles: number
  unchangedVehicles: number
  vehiclesWithValuesBecomingEmpty: number
}

/**
 * VIN-level rollup of the field-mapping stage. Buckets are mutually
 * exclusive and partition `changedVehicles` exactly:
 *
 *   changedVehicles
 *     === valueChangedVehicles
 *       + sourceOnlyChangedVehicles
 *       + auditIntegrityIssues
 *
 * `auditIntegrityIssues` is intentionally NOT a "we don't know" bucket
 * for end users — it surfaces simulation bugs where a VIN was marked as
 * changed without any classified reason. Any non-zero value should be
 * treated as a defect.
 */
export type PublishFieldMappingAuditSummary = {
  changedVehicles: number
  unchangedVehicles: number
  skippedVehicles: number
  erroredVehicles: number
  explainedChangedVehicles: number
  valueChangedVehicles: number
  sourceOnlyChangedVehicles: number
  auditIntegrityIssues: number
  vehicleDataBuckets?: PublishVehicleDataBucketSummary
  fieldSummaries?: PublishFieldMappingAuditFieldSummary[]

  /**
   * Legacy/top transition sample. Do not use for authoritative field summaries.
   */
  topFieldTransitions: PublishFieldMappingAuditTransitionSummary[]

  valueChangedSamples: string[]
  sourceOnlySamples: string[]
  auditIntegritySamples: string[]
}

export type PublishObservabilityWarning = {
  level: 'info' | 'warn' | 'error'
  code: string
  message: string
  stage?: PublishObservedStage
}

export type PublishObservabilitySummary = {
  version: number
  runType: 'preview' | 'publish'
  runId: string
  jazelId: string
  generatedAt: string
  totals: PublishStageCounts
  stages: Partial<Record<PublishObservedStage, PublishStageSummary>>
  auditSummaries?: {
    fieldMapping?: PublishFieldMappingAuditSummary
  }
  /** VIN counts per vehicle-groups bucket when the groups stage ran (summary JSON). */
  vehicleGroupBuckets?: {
    added: number
    removed: number
    changed: number
    unchanged: number
  }
  vehicleGroupingInventoryDelta?: {
    removedVehicleCount: number
    affectedGroupingCount: number
    affectedGroupings: Array<{
      groupId: string
      groupName: string
      removedVehicleCount: number
      sampleVins: string[]
    }>
  }
  listingVisibilityBuckets?: {
    changed: number
    willAppear: number
    willHide: number
    reasonChanged: number
    unchanged: number
  }
  listingVisibilityOutcome?: {
    evaluated: number
    visibleAfterPublish: number
    hiddenAfterPublish: number
    unknownAfterPublish: number
  }
  listingVisibilityInventoryDelta?: {
    evaluated: number
    addedToInventory: number
    removedFromInventory: number
    unchangedInInventory: number
    madeVisible: number
    hidden: number
    unchangedVisibility: number
  }
  websiteImpact?: PublishWebsiteImpactSummary
  warnings?: PublishObservabilityWarning[]
}

export type PublishObservabilityManifest = {
  version: number
  runType: 'preview' | 'publish'
  /** Stable run id; equals `runId` and `simulationId`. */
  id?: string
  runId: string
  jazelId: string
  generatedAt: string
  expiresAt?: string
  retentionClass?: 'preview' | 'publish-history' | 'debug'
  /** Scalar decision coverage over instrumented stages (Publish Flow parity). */
  decisionCoverage?: 'none' | 'partial' | 'full'
  summary: PublishArtifactPointer
  stageDecisions: Partial<Record<PublishObservedStage, PublishArtifactPointer[]>>
  vehicleDataBuckets?: Partial<Record<PublishVehicleDataBucket, PublishArtifactPointer[]>>
  vehicleGroupBuckets?: Partial<Record<PublishVehicleGroupBucket, PublishArtifactPointer[]>>
  listingVisibilityBuckets?: Partial<
    Record<PublishListingVisibilityBucket, PublishArtifactPointer[]>
  >
  workingArtifacts?: Partial<Record<PublishObservedStage, PublishArtifactPointer>>
  stages: Partial<Record<PublishObservedStage, PublishStageSummary>>
  warnings?: PublishObservabilityWarning[]
  /**
   * Metadata ONLY: explains why a Publish Flow stage is intentionally absent
   * from Preview. Must not drive a UI tab/route/card/drilldown or warning by
   * itself.
   */
  excludedStages?: { feedIntake: { reason: string } }
}

export type PublishImportFeedPresignResponse = {
  jazelId: string
  key: string
  fileName: string
  presignedUrl: string
  expiresIn: number
}

/**
 * Field-mapping `changeKind` mirror. See
 * `ise-srp-back/src/publish_preview/types.ts` for the canonical
 * definition. Only field-mapping reasons set this; other stages omit it.
 */
export type PublishFieldMappingChangeKind =
  | 'value'
  | 'source'
  | 'derived'
  | 'representational'

export type PublishDecisionFilterCriterion = {
  field?: string
  actual?: unknown
  expected?: unknown
}

export type PublishDecisionMatchedRuleCriterion = {
  field: string
  operator?: string
  value?: string
}

export type PublishDecisionMatchedRule = {
  ruleIndex?: number
  targetField?: string
  targetFieldLabel?: string
  actionType?: string
  logic?: 'AND' | 'OR'
  match?: PublishDecisionMatchedRuleCriterion[]
  conditionSummary?: string
  actionSummary?: string
  displaySummary?: string
}

export type PublishDecisionReason = {
  code?: string
  configType?: string
  configLabel?: string
  ruleId?: string
  ruleName?: string
  field?: string
  operator?: string
  expectedValue?: unknown
  actualValue?: unknown
  beforeValue?: unknown
  afterValue?: unknown
  sourceBefore?: string
  sourceAfter?: string
  feedsInvolved?: string[]
  transformName?: string
  changeKind?: PublishFieldMappingChangeKind
  matched?: boolean
  matchedRule?: PublishDecisionMatchedRule
  filterIndex?: number
  filterLabel?: string
  criteria?: PublishDecisionFilterCriterion[]
  explanation: string
}

export type PublishListingVisibilityVinDetail = {
  vin: string
  beforeDecision?: 'listed' | 'delisted'
  afterDecision?: 'listed' | 'delisted'
  beforeReason?: string
  afterReason?: string
  matchedRuleBefore?: PublishListingVisibilityMatchedRuleDetail
  matchedRuleAfter?: PublishListingVisibilityMatchedRuleDetail
  reasons: PublishDecisionReason[]
}

export type PublishDecisionRecord = {
  version: number
  runType: 'preview' | 'publish'
  runId: string
  jazelId: string
  vin: string
  stage: PublishObservedStage
  branch: PublishDecisionBranch
  status: PublishDecisionStatus
  before?: unknown
  after?: unknown
  reasons: PublishDecisionReason[]
  provenance?: Record<string, unknown>
  /** Optional read-model field for Feed Preparation VIN provenance. */
  feedSources?: string[]
  generatedAt: string
}

export type PublishParseWarnings = {
  skippedLines: number
  firstLineNumber?: number
  lastLineNumber?: number
  lastError?: string
}

export type PublishParseWarningsForVin = PublishParseWarnings & {
  stages?: PublishObservedStage[]
}

export type PublishOversizedRecordInfo = {
  partIndex: number
  lineNumber: number
  byteSize: number
}

/**
 * One page of stage decision records. `nextCursor` is an opaque token —
 * pass it back verbatim in the `cursor` parameter on the next request.
 */
export type PublishStageDecisionsPage = {
  stage: PublishSupportedDecisionStage
  pageSize: number
  records: PublishDecisionRecord[]
  nextCursor?: string
  parseWarnings?: PublishParseWarnings
  oversizedRecord?: PublishOversizedRecordInfo
}

export type PublishVehicleDataBucketVinRow = {
  vin: string
  changedFieldCount?: number
  emptyingFields?: string[]
}

export type PublishVehicleDataBucketPage = {
  bucket: PublishVehicleDataBucket
  pageSize: number
  records: PublishVehicleDataBucketVinRow[]
  nextCursor?: string
  parseWarnings?: PublishParseWarnings
}

export type PublishVehicleGroupBucketPage = {
  bucket: PublishVehicleGroupBucket
  pageSize: number
  records: PublishVehicleGroupBucketVinRow[]
  nextCursor?: string
  parseWarnings?: PublishParseWarnings
}

export type PublishListingVisibilityBucketPage = {
  bucket: PublishListingVisibilityBucket
  pageSize: number
  records: PublishListingVisibilityBucketVinRow[]
  nextCursor?: string
  parseWarnings?: PublishParseWarnings
}

export type PublishVehicleDataVinFieldDetail = {
  field: string
  previousValue: unknown
  newValue: unknown
  previousSource?: string
  newSource?: string
  valueChanged: boolean
}

export type PublishVehicleDataVinDetail = {
  vin: string
  fields: PublishVehicleDataVinFieldDetail[]
}

export type PublishVinTrailStageEntry = {
  availability: PublishStageAvailability
  /** Present iff availability === 'available' AND a record matched the VIN. */
  record?: PublishDecisionRecord
  /** unsupportedReason / deferredReason from the manifest, when not available. */
  reason?: string
}

export type PublishVinPipelineTrailSource = {
  type: 'feed' | 'manual' | 'unknown'
  feedSource?: string
  summary: string
}

export type PublishVinPipelineTrailEvidence = {
  type: 'preProcessorOutput'
  bucket: string
  key: string
}

export type PublishVinPipelineTrailOutcome =
  | 'passed'
  | 'blocked'
  | 'skipped'
  | 'kept'
  | 'removed'
  | 'changed'
  | 'unchanged'
  | 'shown'
  | 'hidden'
  | 'unsupported'
  | 'unknown'

export type PublishVinPipelineTrailChange = {
  label: string
  before?: unknown
  after?: unknown
}

export type PublishVinPipelineTrailStage = {
  key: PublishObservedStage
  label: string
  outcome: PublishVinPipelineTrailOutcome
  summary: string
  detail?: string
  reasonCode?: string
  changes?: PublishVinPipelineTrailChange[]
}

export type PublishVinPipelineTrail = {
  vin: string
  source: PublishVinPipelineTrailSource
  stages: PublishVinPipelineTrailStage[]
  evidence?: PublishVinPipelineTrailEvidence[]
}

export type PublishVinTrail = {
  vin: string
  stages: Partial<Record<PublishObservedStage, PublishVinTrailStageEntry>>
  pipelineTrail?: PublishVinPipelineTrail
  parseWarnings?: PublishParseWarningsForVin
  truncated?: { cause: 'maxBytes' | 'maxArtifacts'; stage?: PublishObservedStage }
}

// ---------------------------------------------------------------------------
// Fallback discriminated unions returned by the `try*` methods.
// ---------------------------------------------------------------------------

export type ArtifactReadResult<T> =
  | { status: 'ok'; data: T }
  | { status: 'notFound'; code: string; message: string }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route'; message: string }

export type StageDecisionsReadResult =
  | { status: 'ok'; data: PublishStageDecisionsPage }
  | { status: 'notFound'; code: string; message: string }
  | { status: 'notAvailable'; code: 'STAGE_NOT_AVAILABLE'; message: string }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route'; message: string }

export type VehicleDataVinDetailReadResult =
  | { status: 'ok'; data: PublishVehicleDataVinDetail }
  | { status: 'notFound'; code: string; message: string }
  | { status: 'notAvailable'; code: 'STAGE_NOT_AVAILABLE'; message: string }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route'; message: string }

export type VehicleDataBucketReadResult =
  | { status: 'ok'; data: PublishVehicleDataBucketPage }
  | { status: 'notFound'; code: string; message: string }
  | { status: 'notAvailable'; code: 'STAGE_NOT_AVAILABLE'; message: string }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route'; message: string }

export type VehicleGroupBucketReadResult =
  | { status: 'ok'; data: PublishVehicleGroupBucketPage }
  | { status: 'notFound'; code: string; message: string }
  | { status: 'notAvailable'; code: 'STAGE_NOT_AVAILABLE'; message: string }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route'; message: string }

export type VehicleGroupVinDetailReadResult =
  | { status: 'ok'; data: PublishVehicleGroupVinDetail }
  | { status: 'notFound'; code: string; message: string }
  | { status: 'notAvailable'; code: 'STAGE_NOT_AVAILABLE'; message: string }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route'; message: string }

export type ListingVisibilityBucketReadResult =
  | { status: 'ok'; data: PublishListingVisibilityBucketPage }
  | { status: 'notFound'; code: string; message: string }
  | { status: 'notAvailable'; code: 'STAGE_NOT_AVAILABLE'; message: string }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route'; message: string }

export type ListingVisibilityVinDetailReadResult =
  | { status: 'ok'; data: PublishListingVisibilityVinDetail }
  | { status: 'notFound'; code: string; message: string }
  | { status: 'notAvailable'; code: 'STAGE_NOT_AVAILABLE'; message: string }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route'; message: string }

export type PublishFieldMappingAuditFieldSummary = {
  field: string
  affectedVehicles: number
  valueChanges: number
}

// ---------------------------------------------------------------------------
// Internal helpers.
// ---------------------------------------------------------------------------

const trimBase = (endpoint: string) => endpoint.replace(/\/+$/g, '')

const authHeaders = (sessionToken: string) => ({
  Accept: 'application/json',
  Authorization: `Bearer ${sessionToken}`,
})

type RawErrorPayload = {
  error?: string
  message?: string
  code?: string
}

type RawArtifactResponse =
  | { ok: true; data: unknown }
  | { ok: false; status: number; payload: RawErrorPayload | null }

/**
 * Issue a GET against the v2 artifacts API and return either the unwrapped
 * `data` payload (success) or a structured error envelope (any non-2xx).
 *
 * Network errors (fetch reject) ARE NOT caught here — strict callers want
 * the error to propagate, and `try*` callers wrap this in their own
 * try/catch to map to `unavailable: 'network'`.
 *
 * The backend wraps successes as `{ success: true, data: ... }` and
 * errors as `{ error, message, code? }`. We unwrap `data` here so callers
 * never need to know about the envelope.
 */
async function requestArtifact(input: {
  endpoint: string
  path: string
  query?: URLSearchParams
  sessionToken: string
}): Promise<RawArtifactResponse> {
  const base = trimBase(input.endpoint)
  const qs = input.query?.toString()
  const url = `${base}${input.path}${qs && qs.length > 0 ? `?${qs}` : ''}`
  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(input.sessionToken),
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    return { ok: false, status: response.status, payload: (payload ?? null) as RawErrorPayload | null }
  }
  // Phase 3 always returns `{ success: true, data: ... }` for 200s. Defensive:
  // if a future deployment skips the envelope, fall back to the raw payload.
  const data = payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload
  return { ok: true, data }
}

function strictErrorMessage(
  res: { ok: false; status: number; payload: RawErrorPayload | null },
  fallbackPrefix: string,
): string {
  return (
    res.payload?.message ||
    res.payload?.error ||
    `${fallbackPrefix} (${res.status})`
  )
}

/**
 * 4xx codes that ALWAYS throw, regardless of which `try*` wrapper. These
 * represent caller bugs (bad input, bad cursor) or auth problems that
 * the user must see — never silently fall back to legacy.
 */
const ALWAYS_THROW_CODES = new Set<string>([
  'INVALID_STAGE',
  'INVALID_VIN',
  'INVALID_VIN_LENGTH',
  'INVALID_CURSOR',
  'CURSOR_VERSION',
  'CURSOR_MISMATCH',
  'CURSOR_OUT_OF_RANGE',
  'MISSING_PATH_PARAM',
  'INVALID_VEHICLE_DATA_BUCKET',
  'INVALID_VEHICLE_GROUP_BUCKET',
  'INVALID_LISTING_VISIBILITY_BUCKET',
])

function shouldAlwaysThrow(
  res: { ok: false; status: number; payload: RawErrorPayload | null },
): boolean {
  if (res.status === 401 || res.status === 403) return true
  const code = res.payload?.code
  return Boolean(code && ALWAYS_THROW_CODES.has(code))
}

function unavailableReason(
  res: { ok: false; status: number; payload: RawErrorPayload | null },
): { reason: 'http5xx' | 'route'; message: string } | null {
  if (res.status >= 500) {
    return {
      reason: 'http5xx',
      message: strictErrorMessage(res, 'Publish preview artifacts unavailable'),
    }
  }
  // A 404 with NO recognized code means the route is genuinely missing
  // (stale environment / older backend). That's "v2 isn't deployed", not
  // "the artifact does not exist".
  if (res.status === 404 && !res.payload?.code) {
    return {
      reason: 'route',
      message: strictErrorMessage(res, 'Publish preview artifacts route missing'),
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Manifest.
// ---------------------------------------------------------------------------

const MANIFEST_NOT_FOUND_CODES = new Set(['MANIFEST_NOT_FOUND', 'SIMULATION_NOT_FOUND'])

function manifestPath(simulationId: string): string {
  return `/publish-preview/${encodeURIComponent(simulationId)}/manifest`
}

export async function getPublishPreviewManifest(params: {
  endpoint: string
  simulationId: string
  sessionToken: string
}): Promise<PublishObservabilityManifest> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: manifestPath(params.simulationId),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch publish preview manifest'))
  }
  return res.data as PublishObservabilityManifest
}

export async function tryGetPublishPreviewManifest(params: {
  endpoint: string
  simulationId: string
  sessionToken: string
}): Promise<ArtifactReadResult<PublishObservabilityManifest>> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: manifestPath(params.simulationId),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }
  if (res.ok) return { status: 'ok', data: res.data as PublishObservabilityManifest }
  if (shouldAlwaysThrow(res)) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch publish preview manifest'))
  }
  if (res.status === 404 && res.payload?.code && MANIFEST_NOT_FOUND_CODES.has(res.payload.code)) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, 'Publish preview manifest not found'),
    }
  }
  const unavailable = unavailableReason(res)
  if (unavailable) {
    return { status: 'unavailable', ...unavailable }
  }
  throw new Error(strictErrorMessage(res, 'Failed to fetch publish preview manifest'))
}

// ---------------------------------------------------------------------------
// Summary.
// ---------------------------------------------------------------------------

const SUMMARY_NOT_FOUND_CODES = new Set(['SUMMARY_NOT_FOUND', 'SIMULATION_NOT_FOUND'])

function summaryPath(simulationId: string): string {
  return `/publish-preview/${encodeURIComponent(simulationId)}/summary`
}

export async function getPublishPreviewSummary(params: {
  endpoint: string
  simulationId: string
  sessionToken: string
}): Promise<PublishObservabilitySummary> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: summaryPath(params.simulationId),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch publish preview summary'))
  }
  return res.data as PublishObservabilitySummary
}

export async function tryGetPublishPreviewSummary(params: {
  endpoint: string
  simulationId: string
  sessionToken: string
}): Promise<ArtifactReadResult<PublishObservabilitySummary>> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: summaryPath(params.simulationId),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }
  if (res.ok) return { status: 'ok', data: res.data as PublishObservabilitySummary }
  if (shouldAlwaysThrow(res)) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch publish preview summary'))
  }
  if (res.status === 404 && res.payload?.code && SUMMARY_NOT_FOUND_CODES.has(res.payload.code)) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, 'Publish preview summary not found'),
    }
  }
  const unavailable = unavailableReason(res)
  if (unavailable) {
    return { status: 'unavailable', ...unavailable }
  }
  throw new Error(strictErrorMessage(res, 'Failed to fetch publish preview summary'))
}

// ---------------------------------------------------------------------------
// Import feed source file presign.
// ---------------------------------------------------------------------------

export async function getImportFeedFilePresign(params: {
  endpoint: string
  jazelId: string
  key: string
  sessionToken: string
  expiresIn?: number
  bucket?: string
  fileRole?: 'sourceInput' | 'preProcessorOutput' | 'pipelineInput'
  correlationId?: string
  simulationId?: string
}): Promise<PublishImportFeedPresignResponse> {
  const query = new URLSearchParams({
    jazelId: params.jazelId,
    key: params.key,
    expiresIn: String(params.expiresIn ?? 900),
  })
  if (params.bucket) query.set('bucket', params.bucket)
  if (params.fileRole) query.set('fileRole', params.fileRole)
  if (params.correlationId) query.set('correlationId', params.correlationId)
  if (params.simulationId) query.set('simulationId', params.simulationId)
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: '/reports/import-feed-file-presign',
    query,
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(strictErrorMessage(res, 'Failed to generate source feed link'))
  }
  return res.data as PublishImportFeedPresignResponse
}

// ---------------------------------------------------------------------------
// Stage decisions.
// ---------------------------------------------------------------------------

const STAGE_NOT_FOUND_CODES = new Set([
  'STAGE_ARTIFACT_NOT_FOUND',
  'MANIFEST_NOT_FOUND',
  'SIMULATION_NOT_FOUND',
])

function stageDecisionsPath(simulationId: string, stage: PublishSupportedDecisionStage): string {
  return `/publish-preview/${encodeURIComponent(simulationId)}/decisions/${encodeURIComponent(stage)}`
}

function stageDecisionsQuery(input: {
  pageSize?: number
  cursor?: string
}): URLSearchParams | undefined {
  const query = new URLSearchParams()
  if (typeof input.pageSize === 'number' && Number.isFinite(input.pageSize)) {
    query.set('pageSize', String(input.pageSize))
  }
  // Cursor is opaque; round-trip it untouched.
  if (typeof input.cursor === 'string' && input.cursor.length > 0) {
    query.set('cursor', input.cursor)
  }
  return query.toString().length > 0 ? query : undefined
}

export async function getPublishPreviewStageDecisions(params: {
  endpoint: string
  simulationId: string
  stage: PublishSupportedDecisionStage
  sessionToken: string
  pageSize?: number
  cursor?: string
}): Promise<PublishStageDecisionsPage> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: stageDecisionsPath(params.simulationId, params.stage),
    query: stageDecisionsQuery({ pageSize: params.pageSize, cursor: params.cursor }),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(
      strictErrorMessage(res, `Failed to fetch publish preview decisions for ${params.stage}`),
    )
  }
  return res.data as PublishStageDecisionsPage
}

export async function tryGetPublishPreviewStageDecisions(params: {
  endpoint: string
  simulationId: string
  stage: PublishSupportedDecisionStage
  sessionToken: string
  pageSize?: number
  cursor?: string
}): Promise<StageDecisionsReadResult> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: stageDecisionsPath(params.simulationId, params.stage),
      query: stageDecisionsQuery({ pageSize: params.pageSize, cursor: params.cursor }),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }
  if (res.ok) return { status: 'ok', data: res.data as PublishStageDecisionsPage }
  if (shouldAlwaysThrow(res)) {
    throw new Error(
      strictErrorMessage(res, `Failed to fetch publish preview decisions for ${params.stage}`),
    )
  }
  if (res.status === 400 && res.payload?.code === 'STAGE_NOT_AVAILABLE') {
    return {
      status: 'notAvailable',
      code: 'STAGE_NOT_AVAILABLE',
      message: strictErrorMessage(res, `Stage ${params.stage} is not available`),
    }
  }
  if (res.status === 404 && res.payload?.code && STAGE_NOT_FOUND_CODES.has(res.payload.code)) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, `Decisions for ${params.stage} not found`),
    }
  }
  const unavailable = unavailableReason(res)
  if (unavailable) {
    return { status: 'unavailable', ...unavailable }
  }
  throw new Error(
    strictErrorMessage(res, `Failed to fetch publish preview decisions for ${params.stage}`),
  )
}

/**
 * Async iterator that yields each page of stage decisions, chaining
 * `nextCursor` automatically. Iteration ends when the backend stops
 * returning a `nextCursor`.
 *
 * Errors propagate via the strict client, so any caller-driven loop
 * stops on the first error — useful when the caller wants strict
 * semantics. Use the `try*` variants directly if you need fallback
 * behavior on a per-page basis.
 */
export async function* iteratePublishPreviewStageDecisions(params: {
  endpoint: string
  simulationId: string
  stage: PublishSupportedDecisionStage
  sessionToken: string
  pageSize?: number
}): AsyncGenerator<PublishStageDecisionsPage, void, void> {
  let cursor: string | undefined
  do {
    const page = await getPublishPreviewStageDecisions({
      endpoint: params.endpoint,
      simulationId: params.simulationId,
      stage: params.stage,
      sessionToken: params.sessionToken,
      pageSize: params.pageSize,
      cursor,
    })
    yield page
    cursor = page.nextCursor
  } while (cursor)
}

// ---------------------------------------------------------------------------
// Vehicle data buckets.
// ---------------------------------------------------------------------------

const VEHICLE_DATA_BUCKET_NOT_FOUND_CODES = new Set([
  'VEHICLE_DATA_BUCKET_ARTIFACT_NOT_FOUND',
  'MANIFEST_NOT_FOUND',
  'SIMULATION_NOT_FOUND',
])

const VEHICLE_DATA_VIN_DETAIL_NOT_FOUND_CODES = new Set([
  'VEHICLE_DATA_VIN_NOT_FOUND',
  'STAGE_ARTIFACT_NOT_FOUND',
  'MANIFEST_NOT_FOUND',
  'SIMULATION_NOT_FOUND',
])

function vehicleDataBucketPath(simulationId: string): string {
  return `/publish-preview/${encodeURIComponent(simulationId)}/vehicle-data`
}

function vehicleDataVinDetailPath(simulationId: string, vin: string): string {
  return `/publish-preview/${encodeURIComponent(simulationId)}/vehicle-data/vin/${encodeURIComponent(vin)}`
}

function vehicleDataBucketQuery(input: {
  bucket: PublishVehicleDataBucket
  pageSize?: number
  cursor?: string
  search?: string
}): URLSearchParams {
  const query = new URLSearchParams()
  query.set('bucket', input.bucket)
  if (typeof input.pageSize === 'number' && Number.isFinite(input.pageSize)) {
    query.set('pageSize', String(input.pageSize))
  }
  if (typeof input.cursor === 'string' && input.cursor.length > 0) {
    query.set('cursor', input.cursor)
  }
  const search = String(input.search || '').trim()
  if (search.length > 0) {
    query.set('search', search)
  }
  return query
}

export async function getPublishPreviewVehicleDataBucket(params: {
  endpoint: string
  simulationId: string
  bucket: PublishVehicleDataBucket
  sessionToken: string
  pageSize?: number
  cursor?: string
  search?: string
}): Promise<PublishVehicleDataBucketPage> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: vehicleDataBucketPath(params.simulationId),
    query: vehicleDataBucketQuery({
      bucket: params.bucket,
      pageSize: params.pageSize,
      cursor: params.cursor,
      search: params.search,
    }),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(
      strictErrorMessage(res, `Failed to fetch vehicle data bucket ${params.bucket}`),
    )
  }
  return res.data as PublishVehicleDataBucketPage
}

export async function tryGetPublishPreviewVehicleDataBucket(params: {
  endpoint: string
  simulationId: string
  bucket: PublishVehicleDataBucket
  sessionToken: string
  pageSize?: number
  cursor?: string
  search?: string
}): Promise<VehicleDataBucketReadResult> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: vehicleDataBucketPath(params.simulationId),
      query: vehicleDataBucketQuery({
        bucket: params.bucket,
        pageSize: params.pageSize,
        cursor: params.cursor,
        search: params.search,
      }),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }

  if (res.ok) return { status: 'ok', data: res.data as PublishVehicleDataBucketPage }

  if (shouldAlwaysThrow(res)) {
    throw new Error(
      strictErrorMessage(res, `Failed to fetch vehicle data bucket ${params.bucket}`),
    )
  }

  if (res.status === 400 && res.payload?.code === 'STAGE_NOT_AVAILABLE') {
    return {
      status: 'notAvailable',
      code: 'STAGE_NOT_AVAILABLE',
      message: strictErrorMessage(res, 'Vehicle data is not available'),
    }
  }

  if (
    res.status === 404 &&
    res.payload?.code &&
    VEHICLE_DATA_BUCKET_NOT_FOUND_CODES.has(res.payload.code)
  ) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, `Vehicle data bucket ${params.bucket} not found`),
    }
  }

  const unavailable = unavailableReason(res)
  if (unavailable) {
    return { status: 'unavailable', ...unavailable }
  }

  throw new Error(
    strictErrorMessage(res, `Failed to fetch vehicle data bucket ${params.bucket}`),
  )
}

const VEHICLE_GROUP_BUCKET_NOT_FOUND_CODES = new Set([
  'VEHICLE_GROUP_BUCKET_ARTIFACT_NOT_FOUND',
  'MANIFEST_NOT_FOUND',
  'SIMULATION_NOT_FOUND',
])

const VEHICLE_GROUP_VIN_DETAIL_NOT_FOUND_CODES = new Set([
  'VEHICLE_GROUP_VIN_NOT_FOUND',
  'STAGE_ARTIFACT_NOT_FOUND',
  'MANIFEST_NOT_FOUND',
  'SIMULATION_NOT_FOUND',
])

const LISTING_VISIBILITY_BUCKET_NOT_FOUND_CODES = new Set([
  'LISTING_VISIBILITY_BUCKET_ARTIFACT_NOT_FOUND',
  'MANIFEST_NOT_FOUND',
  'SIMULATION_NOT_FOUND',
])

const LISTING_VISIBILITY_VIN_DETAIL_NOT_FOUND_CODES = new Set([
  'LISTING_VISIBILITY_VIN_NOT_FOUND',
  'STAGE_ARTIFACT_NOT_FOUND',
  'MANIFEST_NOT_FOUND',
  'SIMULATION_NOT_FOUND',
])

function vehicleGroupBucketPath(simulationId: string): string {
  return `/publish-preview/${encodeURIComponent(simulationId)}/vehicle-groups`
}

function vehicleGroupVinDetailPath(simulationId: string, vin: string): string {
  return `/publish-preview/${encodeURIComponent(simulationId)}/vehicle-groups/vin/${encodeURIComponent(vin)}`
}

function vehicleGroupBucketQuery(input: {
  bucket: PublishVehicleGroupBucket
  pageSize?: number
  cursor?: string
  search?: string
}): URLSearchParams {
  const query = new URLSearchParams()
  query.set('bucket', input.bucket)
  if (typeof input.pageSize === 'number' && Number.isFinite(input.pageSize)) {
    query.set('pageSize', String(input.pageSize))
  }
  if (typeof input.cursor === 'string' && input.cursor.length > 0) {
    query.set('cursor', input.cursor)
  }
  const search = String(input.search || '').trim()
  if (search.length > 0) {
    query.set('search', search)
  }
  return query
}

function listingVisibilityBucketPath(simulationId: string): string {
  return `/publish-preview/${encodeURIComponent(simulationId)}/listing-visibility`
}

function listingVisibilityVinDetailPath(simulationId: string, vin: string): string {
  return `/publish-preview/${encodeURIComponent(simulationId)}/listing-visibility/vin/${encodeURIComponent(vin)}`
}

function listingVisibilityBucketQuery(input: {
  bucket: PublishListingVisibilityBucket
  pageSize?: number
  cursor?: string
  search?: string
}): URLSearchParams {
  const query = new URLSearchParams()
  query.set('bucket', input.bucket)
  if (typeof input.pageSize === 'number' && Number.isFinite(input.pageSize)) {
    query.set('pageSize', String(input.pageSize))
  }
  if (typeof input.cursor === 'string' && input.cursor.length > 0) {
    query.set('cursor', input.cursor)
  }
  const search = String(input.search || '').trim()
  if (search.length > 0) query.set('search', search)
  return query
}

export async function getPublishPreviewVehicleGroupBucket(params: {
  endpoint: string
  simulationId: string
  bucket: PublishVehicleGroupBucket
  sessionToken: string
  pageSize?: number
  cursor?: string
  search?: string
}): Promise<PublishVehicleGroupBucketPage> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: vehicleGroupBucketPath(params.simulationId),
    query: vehicleGroupBucketQuery({
      bucket: params.bucket,
      pageSize: params.pageSize,
      cursor: params.cursor,
      search: params.search,
    }),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(
      strictErrorMessage(res, `Failed to fetch vehicle group bucket ${params.bucket}`),
    )
  }
  return res.data as PublishVehicleGroupBucketPage
}

export async function tryGetPublishPreviewVehicleGroupBucket(params: {
  endpoint: string
  simulationId: string
  bucket: PublishVehicleGroupBucket
  sessionToken: string
  pageSize?: number
  cursor?: string
  search?: string
}): Promise<VehicleGroupBucketReadResult> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: vehicleGroupBucketPath(params.simulationId),
      query: vehicleGroupBucketQuery({
        bucket: params.bucket,
        pageSize: params.pageSize,
        cursor: params.cursor,
        search: params.search,
      }),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }

  if (res.ok) return { status: 'ok', data: res.data as PublishVehicleGroupBucketPage }

  if (shouldAlwaysThrow(res)) {
    throw new Error(
      strictErrorMessage(res, `Failed to fetch vehicle group bucket ${params.bucket}`),
    )
  }

  if (res.status === 400 && res.payload?.code === 'STAGE_NOT_AVAILABLE') {
    return {
      status: 'notAvailable',
      code: 'STAGE_NOT_AVAILABLE',
      message: strictErrorMessage(res, 'Vehicle groups are not available'),
    }
  }

  if (
    res.status === 404 &&
    res.payload?.code &&
    VEHICLE_GROUP_BUCKET_NOT_FOUND_CODES.has(res.payload.code)
  ) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, `Vehicle group bucket ${params.bucket} not found`),
    }
  }

  const unavailable = unavailableReason(res)
  if (unavailable) {
    return { status: 'unavailable', ...unavailable }
  }

  throw new Error(
    strictErrorMessage(res, `Failed to fetch vehicle group bucket ${params.bucket}`),
  )
}

export async function getPublishPreviewVehicleDataVinDetail(params: {
  endpoint: string
  simulationId: string
  vin: string
  sessionToken: string
}): Promise<PublishVehicleDataVinDetail> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: vehicleDataVinDetailPath(params.simulationId, params.vin),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(
      strictErrorMessage(res, 'Failed to fetch vehicle data VIN detail'),
    )
  }
  return res.data as PublishVehicleDataVinDetail
}

export async function tryGetPublishPreviewVehicleDataVinDetail(params: {
  endpoint: string
  simulationId: string
  vin: string
  sessionToken: string
}): Promise<VehicleDataVinDetailReadResult> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: vehicleDataVinDetailPath(params.simulationId, params.vin),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }

  if (res.ok) return { status: 'ok', data: res.data as PublishVehicleDataVinDetail }

  if (shouldAlwaysThrow(res)) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch vehicle data VIN detail'))
  }

  if (res.status === 400 && res.payload?.code === 'STAGE_NOT_AVAILABLE') {
    return {
      status: 'notAvailable',
      code: 'STAGE_NOT_AVAILABLE',
      message: strictErrorMessage(res, 'Vehicle data is not available'),
    }
  }

  if (
    res.status === 404 &&
    res.payload?.code &&
    VEHICLE_DATA_VIN_DETAIL_NOT_FOUND_CODES.has(res.payload.code)
  ) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, 'Vehicle data VIN detail not found'),
    }
  }

  const unavailable = unavailableReason(res)
  if (unavailable) {
    return { status: 'unavailable', ...unavailable }
  }

  throw new Error(strictErrorMessage(res, 'Failed to fetch vehicle data VIN detail'))
}

export async function getPublishPreviewVehicleGroupVinDetail(params: {
  endpoint: string
  simulationId: string
  vin: string
  sessionToken: string
}): Promise<PublishVehicleGroupVinDetail> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: vehicleGroupVinDetailPath(params.simulationId, params.vin),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(
      strictErrorMessage(res, 'Failed to fetch vehicle group VIN detail'),
    )
  }
  return res.data as PublishVehicleGroupVinDetail
}

export async function tryGetPublishPreviewVehicleGroupVinDetail(params: {
  endpoint: string
  simulationId: string
  vin: string
  sessionToken: string
}): Promise<VehicleGroupVinDetailReadResult> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: vehicleGroupVinDetailPath(params.simulationId, params.vin),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }

  if (res.ok) return { status: 'ok', data: res.data as PublishVehicleGroupVinDetail }

  if (shouldAlwaysThrow(res)) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch vehicle group VIN detail'))
  }

  if (res.status === 400 && res.payload?.code === 'STAGE_NOT_AVAILABLE') {
    return {
      status: 'notAvailable',
      code: 'STAGE_NOT_AVAILABLE',
      message: strictErrorMessage(res, 'Vehicle groups are not available'),
    }
  }

  if (
    res.status === 404 &&
    res.payload?.code &&
    VEHICLE_GROUP_VIN_DETAIL_NOT_FOUND_CODES.has(res.payload.code)
  ) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, 'Vehicle group VIN detail not found'),
    }
  }

  const unavailable = unavailableReason(res)
  if (unavailable) {
    return { status: 'unavailable', ...unavailable }
  }

  throw new Error(strictErrorMessage(res, 'Failed to fetch vehicle group VIN detail'))
}

export async function getPublishPreviewListingVisibilityBucket(params: {
  endpoint: string
  simulationId: string
  bucket: PublishListingVisibilityBucket
  sessionToken: string
  pageSize?: number
  cursor?: string
  search?: string
}): Promise<PublishListingVisibilityBucketPage> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: listingVisibilityBucketPath(params.simulationId),
    query: listingVisibilityBucketQuery(params),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(
      strictErrorMessage(res, `Failed to fetch listing visibility bucket ${params.bucket}`),
    )
  }
  return res.data as PublishListingVisibilityBucketPage
}

export async function tryGetPublishPreviewListingVisibilityBucket(params: {
  endpoint: string
  simulationId: string
  bucket: PublishListingVisibilityBucket
  sessionToken: string
  pageSize?: number
  cursor?: string
  search?: string
}): Promise<ListingVisibilityBucketReadResult> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: listingVisibilityBucketPath(params.simulationId),
      query: listingVisibilityBucketQuery(params),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }

  if (res.ok) return { status: 'ok', data: res.data as PublishListingVisibilityBucketPage }

  if (shouldAlwaysThrow(res)) {
    throw new Error(
      strictErrorMessage(res, `Failed to fetch listing visibility bucket ${params.bucket}`),
    )
  }

  if (res.status === 400 && res.payload?.code === 'STAGE_NOT_AVAILABLE') {
    return {
      status: 'notAvailable',
      code: 'STAGE_NOT_AVAILABLE',
      message: strictErrorMessage(res, 'Listing visibility is not available'),
    }
  }

  if (
    res.status === 404 &&
    res.payload?.code &&
    LISTING_VISIBILITY_BUCKET_NOT_FOUND_CODES.has(res.payload.code)
  ) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, 'Listing visibility bucket not found'),
    }
  }

  const unavailable = unavailableReason(res)
  if (unavailable) return { status: 'unavailable', ...unavailable }

  throw new Error(
    strictErrorMessage(res, `Failed to fetch listing visibility bucket ${params.bucket}`),
  )
}

export async function getPublishPreviewListingVisibilityVinDetail(params: {
  endpoint: string
  simulationId: string
  vin: string
  sessionToken: string
}): Promise<PublishListingVisibilityVinDetail> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: listingVisibilityVinDetailPath(params.simulationId, params.vin),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(
      strictErrorMessage(res, `Failed to fetch listing visibility detail for ${params.vin}`),
    )
  }
  return res.data as PublishListingVisibilityVinDetail
}

export async function tryGetPublishPreviewListingVisibilityVinDetail(params: {
  endpoint: string
  simulationId: string
  vin: string
  sessionToken: string
}): Promise<ListingVisibilityVinDetailReadResult> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: listingVisibilityVinDetailPath(params.simulationId, params.vin),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }

  if (res.ok) return { status: 'ok', data: res.data as PublishListingVisibilityVinDetail }

  if (shouldAlwaysThrow(res)) {
    throw new Error(
      strictErrorMessage(res, `Failed to fetch listing visibility detail for ${params.vin}`),
    )
  }

  if (res.status === 400 && res.payload?.code === 'STAGE_NOT_AVAILABLE') {
    return {
      status: 'notAvailable',
      code: 'STAGE_NOT_AVAILABLE',
      message: strictErrorMessage(res, 'Listing visibility is not available'),
    }
  }

  if (
    res.status === 404 &&
    res.payload?.code &&
    LISTING_VISIBILITY_VIN_DETAIL_NOT_FOUND_CODES.has(res.payload.code)
  ) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, 'Listing visibility detail not found'),
    }
  }

  const unavailable = unavailableReason(res)
  if (unavailable) return { status: 'unavailable', ...unavailable }

  throw new Error(
    strictErrorMessage(res, `Failed to fetch listing visibility detail for ${params.vin}`),
  )
}

// ---------------------------------------------------------------------------
// VIN trail.
// ---------------------------------------------------------------------------

const VIN_TRAIL_NOT_FOUND_CODES = new Set(['MANIFEST_NOT_FOUND', 'SIMULATION_NOT_FOUND'])

function vinTrailPath(simulationId: string, vin: string): string {
  // VIN is `encodeURIComponent`-encoded but NOT uppercased here; the
  // backend handles case-insensitive matching after URL-decoding.
  return `/publish-preview/${encodeURIComponent(simulationId)}/vins/${encodeURIComponent(vin)}`
}

export async function getPublishPreviewVinTrail(params: {
  endpoint: string
  simulationId: string
  vin: string
  sessionToken: string
}): Promise<PublishVinTrail> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: vinTrailPath(params.simulationId, params.vin),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch publish preview VIN trail'))
  }
  return res.data as PublishVinTrail
}

export async function tryGetPublishPreviewVinTrail(params: {
  endpoint: string
  simulationId: string
  vin: string
  sessionToken: string
}): Promise<ArtifactReadResult<PublishVinTrail>> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: vinTrailPath(params.simulationId, params.vin),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }
  if (res.ok) return { status: 'ok', data: res.data as PublishVinTrail }
  if (shouldAlwaysThrow(res)) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch publish preview VIN trail'))
  }
  if (res.status === 404 && res.payload?.code && VIN_TRAIL_NOT_FOUND_CODES.has(res.payload.code)) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, 'Publish preview VIN trail not found'),
    }
  }
  const unavailable = unavailableReason(res)
  if (unavailable) {
    return { status: 'unavailable', ...unavailable }
  }
  throw new Error(strictErrorMessage(res, 'Failed to fetch publish preview VIN trail'))
}
